/* eslint-disable complexity */
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { URLSearchParamsInit, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TicketHolder } from '@components/customers/customers-table-expandable/src/customers-table-expandable.type';
import { EventAreaChartSectionData } from '@components/event-area-chart-section/src/event-area-chart-section.type';
import { EventLinearChartSectionData } from '@components/event-linear-chart-section/src/event-linear-chart-section.type';
import { OfferTableSectionDataType } from '@components/event-offer-table-section/src/event-offer-table-section.type';
import { ChannelSectionDataType } from '@components/event-report-channel-section/src/event-report-channel-section.type';
import { HightLightStat } from '@components/event-report-highlight-section/src/event-report-highlight-section.type';
import {
  ENABLED_BOOKING_DETAILS_ROUTE,
  ENABLED_DOWNLOAD_BOOKING_PDF,
  ENABLED_REFUND_BUTTON,
  ENABLED_REFUND_STATS,
  ENABLED_REPORT_STATS,
  ENABLED_RESEND_EMAIL_BUTTON,
} from '@constants/company-auth-permissions';
import { DATE_FORMAT_LONG_DATE, DEFAULT_TIMEZONE } from '@constants/datetime';
import { FILE_TYPE_CSV, SEPARATOR_TYPE_COMMA, SORT_ASC } from '@constants/global';
import { EVENT_REPORT_DOWNLOAD_PDF_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { LAYOUT_WITHOUT_SIDEBAR } from '@contexts/layout/layout.type';
import { LayoutContext } from '@contexts/layout/layout-context';
import { SHARED_REPORT_TOKEN_QUERY_PARAM } from '@contexts/user/user.type';
import { UserContext } from '@contexts/user/user-context';
import {
  EventReportActionType,
  EventReportDataType,
  EventSalesStatsAddon,
  EventSalesStatsOffer,
  LinearGraphGroupBy,
} from '@domain/event-report';
import { useGetAddons } from '@hooks/addons/use-get-addons';
import { useGetAdmissions } from '@hooks/admissions/use-get-admissions';
import { useCurrentEvent } from '@hooks/events/use-current-event';
import { useExportBookings } from '@hooks/events/use-export-bookings';
import { useGetEventAttendeesStats } from '@hooks/events/use-get-attendees-time-stats';
import { EventBookingParams, useGetEventBookings } from '@hooks/events/use-get-event-bookings';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetEventChannelsSalesStats } from '@hooks/events/use-get-event-channels-sales-stats';
import { useGetEventSalesStats } from '@hooks/events/use-get-event-sales-stats';
import { useGetEventSalesStatsAddons } from '@hooks/events/use-get-event-sales-stats-addons';
import { useGetEventSalesStatsGroupBy } from '@hooks/events/use-get-event-sales-stats-groupby';
import { useGetEventViewsStatsGroupBy } from '@hooks/events/use-get-event-views-stats';
import { useGetEventTeamChannels } from '@hooks/team-channel/use-get-event-team-channels';
import { ExportType, SeparatorType } from '@hooks/use-customers-page-reducer/use-customers-page-reducer-types';
import { VIEW_PAST, VIEW_UPCOMING } from '@hooks/use-event-navigation/use-event-navigation';
import { useIsProtectedElement } from '@hooks/use-is-protected-element';
import { getEventState } from '@pages/events/utils/reports-utils';
import { formatDataAreaChartSection } from '@pages/events/utils/reports-utils/area-chart-section';
import { formatDataBookingMixSection } from '@pages/events/utils/reports-utils/booking-mix-section';
import { formatDataChannelSection } from '@pages/events/utils/reports-utils/channel-section';
import {
  formatDataBookingTableSection,
  formatFiltersBookingTableSection,
  getBookingDetailsRoute,
} from '@pages/events/utils/reports-utils/community-section';
import {
  ChannelDataTypeTabs,
  EVENT_REPORT_ACTION_TYPE_CANCEL,
  EVENT_REPORT_ACTION_TYPE_DOWNLOAD_PDF,
  EVENT_REPORT_ACTION_TYPE_REFUND,
  EVENT_REPORT_ACTION_TYPE_RESEND_BOOKING,
  EVENT_REPORT_ACTION_TYPE_SEE_DETAILS,
  EVENT_REPORT_ADDON_TYPE,
  EVENT_REPORT_ATTENDANCE_TYPE_ENTRY_TIME,
  EVENT_REPORT_BOTTLE_SERVICE_TYPE,
  EVENT_REPORT_CHECKIN_TYPE,
  EVENT_REPORT_GUEST_LIST_TYPE,
  EVENT_REPORT_INVITATION_TYPE,
  EVENT_REPORT_MODE_TYPE_LIST,
  EVENT_REPORT_PASS_TYPE,
  EVENT_REPORT_TAB_ADMISSION,
  EVENT_REPORT_TAB_TIME_DAY,
  EVENT_REPORT_TAB_TYPE_BOOKING,
  EVENT_REPORT_TAB_TYPE_VIEWS,
  EVENT_REPORT_TABLE_NAME_COLUMN,
  EVENT_REPORT_TICKET_TYPE,
  EVENT_REPORT_TYPES,
} from '@pages/events/utils/reports-utils/config';
import { formatDataHighlightSection } from '@pages/events/utils/reports-utils/highlight-section';
import { formatDataLinearChartSection } from '@pages/events/utils/reports-utils/linear-chart-section';
import { formatDataOfferTableSection } from '@pages/events/utils/reports-utils/offers-table-section';
import {
  BookingActionMethodProps,
  BookingActions,
  EventReportAreaTypes,
  EventReportLinearTypes,
  SelectedBookingEmail,
} from '@pages/events/utils/reports-utils/types';
import { getDatetimesFromToValues } from '@utils/get-datetime-from-to-values /get-datetime-from-to-values';
import { toBackendWithTimezone } from '@utils/map-date/to-backend';
import { getPublicPageToken } from '@utils/token/authTokens';
import { PUBLIC_PAGE_TOKEN } from '@utils/token/constants';
import moment from 'moment';

import {
  ALL_TIME_OPTIONS,
  bookingOptionsActions,
  BookingTableSectionData,
  EventBookingMixSectionInfo,
  EventReportBookingOption,
  EventReportBookingOptions,
  EventReportContextValue,
  EventReportFilter,
  EventReportQueryParams,
  EventReportTimeOption,
  pastTimeOptionsActions,
  SELECT_RANGE_OPTIONS,
  timeOptionsActions,
} from './event-report-context.type';

export const EventReportContext = createContext<EventReportContextValue>({
  attendeesStats: undefined,
  event: undefined,
  eventIsLoading: false,
  eventSales: undefined,
  eventSalesIsLoading: false,
  eventSalesGroupedBy: undefined,
  eventSalesGroupedByIsLoading: false,
  eventSalesChannelsIsLoading: false,
  attendeesStatsIsLoading: false,
  eventViewsIsLoading: false,
  eventChannels: undefined,
  eventTeams: undefined,
  offerTableSectionData: {
    data: undefined,
    addons: undefined,
    addonBookings: undefined,
    tabs: [],
    defaultTab: EVENT_REPORT_TAB_ADMISSION,
    tableColumns: [],
  },
  hightlightSectionData: [],
  areaChartSectionData: {
    data: [],
    categories: [],
    dataTypeTabs: [],
    defaultDataTypeTab: EVENT_REPORT_TAB_TYPE_BOOKING,
  },
  lineChartSectionData: {
    data: [],
    categories: [],
    dataTypeTabs: [],
    defaultTimeTab: EVENT_REPORT_TAB_TIME_DAY,
    defaultDataTypeTab: EVENT_REPORT_TAB_TYPE_BOOKING,
    timeTabs: [],
  },
  channelSectionData: {
    data: [],
    chartData: [],
    categories: [],
    tableColumns: {
      type: '',
      isDropdown: false,
      isFooterEnabled: false,
      columns: [],
      expandColumns: [],
    },
    dataTypeTabs: [],
    defaultDataTypeTab: EVENT_REPORT_TAB_TYPE_BOOKING,
    modeTypeTabs: [],
    defaultModeTypeTab: EVENT_REPORT_MODE_TYPE_LIST,
  },
  bookingTableSectionData: {
    data: [],
    filters: [],
    fetchNextPage: () => {},
    hasNextPage: false,
    isLoading: false,
    isFetching: false,
    isFetchingNextPage: false,
    exportType: FILE_TYPE_CSV,
    setExportType: () => {},
    separatorType: SEPARATOR_TYPE_COMMA,
    setSeparatorType: () => {},
  },
  exportData: undefined,
  isExportBookingLoading: false,
  refetchExportFile: () => {},
  bookingTableParams: {},
  setBookingTableParams: () => {},
  filter: undefined,
  setFilters: () => {},
  eventSalesGroupBy: undefined,
  setEventSalesGroupBy: () => {},
  setQueryParams: () => {},
  timeOptions: [],
  timeOptionSelected: undefined,
  bookingMixSectionData: { data: [], categories: [] },
  bookingOptions: [],
  bookingOptionSelected: undefined,
  selectedChannels: [],
  setActiveLinearTabDataType: () => {},
  activeLinearTabDataType: EVENT_REPORT_TAB_TYPE_BOOKING,
  isAddonModalOpen: false,
  isCancelModalOpen: false,
  setIsCancelModalOpen: () => {},
  isPassHoldersModalOpen: false,
  toggleModalBy: () => {},
  selectedOffer: undefined,
  selectedBookingEmail: undefined,
  setSelectedBookingEmail: () => {},
  refundData: {
    isOpenModal: false,
  },
  selectedBookingCancel: undefined,
  setRefundData: () => {},
  activeChannelTab: EVENT_REPORT_TAB_TYPE_BOOKING,
  setActiveChannelTab: () => {},
  activeAreaTabDataType: EVENT_REPORT_ATTENDANCE_TYPE_ENTRY_TIME,
  setActiveAreaTabDataType: () => {},
});

export const EventReportProvider = ({ children, isInfoPoint }: { children: ReactNode; isInfoPoint?: boolean }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { userState } = useContext(UserContext);
  const { isPublicEventReportPage } = useContext(FeatureFlagContext);
  const showReportStats = useIsProtectedElement({ permissions: ENABLED_REPORT_STATS }) || isPublicEventReportPage;
  const isRefundStatsEnabled = useIsProtectedElement({ permissions: ENABLED_REFUND_STATS });
  const { eventId } = useParams<{ companyId: string; clubSlug: string; eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const channelsParam = searchParams.get('channels');
  const datetimeParam = searchParams.get('datetime');
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const bookingParam = searchParams.get('bookings');

  const { setLayout } = useContext(LayoutContext);
  const {
    clubState,
    companyState: { legacyId: companyLegacyId },
  } = useContext(CompanyClubContext);
  const { slug: clubSlug } = clubState;

  const [filter, setFilter] = useState<EventReportFilter>();
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [timeOptionSelected, setTimeOptionSelected] = useState<EventReportTimeOption | undefined>();
  const [bookingOptionSelected, setBookingOptionSelected] = useState<EventReportBookingOption>();
  const [exportType, setExportType] = useState<ExportType>(FILE_TYPE_CSV);
  const [separatorType, setSeparatorType] = useState<SeparatorType>(SEPARATOR_TYPE_COMMA);
  const [eventSalesGroupBy, setEventSalesGroupBy] = useState<LinearGraphGroupBy>(EVENT_REPORT_TAB_TIME_DAY);
  const [selectedOffer, setSelectedOffer] = useState<EventSalesStatsOffer | EventSalesStatsAddon | undefined>(
    undefined
  );
  const [activeChannelTab, setActiveChannelTab] = useState<ChannelDataTypeTabs>(EVENT_REPORT_TAB_TYPE_BOOKING);
  const [selectedBookingEmail, setSelectedBookingEmail] = useState<SelectedBookingEmail>();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [selectedBookingCancel, setSelectedBookingCancel] = useState<string | undefined>();
  const [isAddonModalOpen, setIsAddonModalOpen] = useState<boolean>(false);
  const [refundData, setRefundData] = useState<{
    isOpenModal: boolean;
    data?: {
      holderCode?: string;
      quantity: number;
      bookingId: string;
      legacyId?: number;
      ticketHolders: TicketHolder[];
      onlinePrice: number;
      offerType: string;
      isNominative: boolean;
      buyerFirstName: string;
      buyerLastName: string;
    };
  }>({ isOpenModal: false });
  const [isPassHoldersModalOpen, setIsPassHoldersModalOpen] = useState<boolean>(false);
  const [activeLinearTabDataType, setActiveLinearTabDataType] =
    useState<EventReportLinearTypes>(EVENT_REPORT_TAB_TYPE_BOOKING);
  const [activeAreaTabDataType, setActiveAreaTabDataType] = useState<EventReportAreaTypes>(
    EVENT_REPORT_ATTENDANCE_TYPE_ENTRY_TIME
  );
  const [bookingMixSectionData, setBookingMixSectionData] = useState<EventBookingMixSectionInfo>({
    data: [],
    categories: [],
  });
  const [channelSectionData, setChannelSectionData] = useState<ChannelSectionDataType>({
    data: [],
    chartData: [],
    categories: [],
    tableColumns: {
      type: '',
      isDropdown: false,
      isFooterEnabled: false,
      columns: [],
      expandColumns: [],
    },
    dataTypeTabs: [],
    defaultDataTypeTab: EVENT_REPORT_TAB_TYPE_BOOKING,
    modeTypeTabs: [],
    defaultModeTypeTab: EVENT_REPORT_MODE_TYPE_LIST,
  });
  const [offerTableSectionData, setOfferTableSectionData] = useState<OfferTableSectionDataType>({
    data: undefined,
    addons: undefined,
    addonBookings: undefined,
    tabs: [],
    defaultTab: EVENT_REPORT_TAB_ADMISSION,
    tableColumns: [],
  });
  const [hightlightSectionData, setHightlightSectionData] = useState<HightLightStat[]>([]);
  const [areaChartSectionData, setAreaChartSectionData] = useState<EventAreaChartSectionData>({
    data: [],
    categories: [],
    defaultDataTypeTab: EVENT_REPORT_TAB_TYPE_BOOKING,
    dataTypeTabs: [],
  });
  const [lineChartSectionData, setLineChartSectionData] = useState<EventLinearChartSectionData>({
    data: [],
    categories: [],
    defaultTimeTab: EVENT_REPORT_TAB_TIME_DAY,
    defaultDataTypeTab: EVENT_REPORT_TAB_TYPE_BOOKING,
    dataTypeTabs: [],
    timeTabs: [],
  });
  const [bookingTableParams, setBookingTableParams] = useState<EventBookingParams>({
    orderBy: EVENT_REPORT_TABLE_NAME_COLUMN,
    sort: SORT_ASC,
  });
  const [addonTableParams, setAddonTableParams] = useState<EventBookingParams>({
    orderBy: EVENT_REPORT_TABLE_NAME_COLUMN,
    sort: SORT_ASC,
  });
  const [bookingTableSectionData, setBookingTableSectionData] = useState<BookingTableSectionData>({
    data: [],
    filters: [],
    fetchNextPage: () => {},
    hasNextPage: false,
    isLoading: false,
    isFetching: false,
    isFetchingNextPage: false,
    exportType: FILE_TYPE_CSV,
    setExportType,
    separatorType: SEPARATOR_TYPE_COMMA,
    setSeparatorType,
  });

  const { event, isLoading: eventIsLoading } = useCurrentEvent();

  const eventReportState = getEventState(event ?? undefined);
  const isEnabledRequest = !!eventId && !!event && !isInfoPoint;
  const isEnabledFilteredRequest =
    isEnabledRequest && !!filter && (eventReportState === VIEW_PAST ? !!filter?.to : true) && showReportStats;
  const timezone = event?.club.city.timezone ?? DEFAULT_TIMEZONE;

  const isEnabledSeeDetails = useIsProtectedElement({ permissions: ENABLED_BOOKING_DETAILS_ROUTE });
  const isEnabledResendEmail = useIsProtectedElement({ permissions: ENABLED_RESEND_EMAIL_BUTTON });
  const isEnabledRefundButton = useIsProtectedElement({ permissions: ENABLED_REFUND_BUTTON });
  const isEnabledDownloadPDF = useIsProtectedElement({ permissions: ENABLED_DOWNLOAD_BOOKING_PDF });

  const { data: admissions } = useGetAdmissions({ eventId: event?.id ?? '', options: { enabled: !!event?.id } });

  const { data: addons } = useGetAddons({ eventId: event?.id ?? '', enabled: !!event?.id });

  const { data: eventSales, isLoading: eventSalesIsLoading } = useGetEventSalesStats({
    eventId,
    params: { ...filter, booking: undefined },
    options: { enabled: isEnabledFilteredRequest },
  });

  const { data: eventSalesGroupedBy, isLoading: eventSalesGroupedByIsLoading } = useGetEventSalesStatsGroupBy({
    eventId,
    params: {
      ...filter,
      booking: undefined,
      groupByDate: eventSalesGroupBy,
    },
    options: { enabled: isEnabledFilteredRequest },
  });

  const { data: eventSalesChannels, isLoading: eventSalesChannelsIsLoading } = useGetEventChannelsSalesStats({
    eventId,
    params: {
      ...filter,
      booking: undefined,
    },
    options: { enabled: isEnabledFilteredRequest },
  });

  const { data: eventSalesStatsAddons } = useGetEventSalesStatsAddons({
    eventId,
    params: { ...filter, booking: undefined },
    options: { enabled: isEnabledFilteredRequest },
  });

  const { data: eventChannels } = useGetChannels({
    eventId,
    enabled: isEnabledRequest,
  });

  const {
    data: eventBookings,
    isLoading: isLoadingBookings,
    fetchNextPage: fetchNexPageBookings,
    hasNextPage: hasNextPageBookings,
    isFetching: isFetchingBookings,
    isFetchingNextPage: isFetchingNextPageBookings,
  } = useGetEventBookings({
    eventId: event?.id ?? '',
    params: { ...filter, ...bookingTableParams, salesMode: filter?.booking, booking: undefined },
    options: { enabled: !!event && !!filter && !isPublicEventReportPage },
  });

  const { data: attendeesStats, isLoading: attendeesStatsIsLoading } = useGetEventAttendeesStats({
    eventId,
    params: { ...filter, booking: undefined },
    options: { enabled: isEnabledFilteredRequest && eventReportState !== VIEW_UPCOMING },
  });

  const {
    data: addonBookings,
    isLoading: isLoadingAddonBookings,
    fetchNextPage: fetchNextpageAddonBookings,
    hasNextPage: hasNextPageAddonBookings,
    isFetching: isFetchingAddonBookings,
    isFetchingNextPage: isFetchingNextPageAddonBookings,
  } = useGetEventBookings({
    eventId: event?.id ?? '',
    params: {
      ...filter,
      sort: addonTableParams.sort,
      orderBy: addonTableParams.orderBy,
      limit: addonTableParams.limit,
      offset: addonTableParams.offset,
      salesMode: filter?.booking,
      addOns: [selectedOffer?.id ?? ''],
      booking: undefined,
    },
    options: { enabled: !!event && !!selectedOffer && isAddonModalOpen },
  });

  const {
    data: exportData,
    isLoading: isExportBookingLoading,
    refetch: refetchExportFile,
  } = useExportBookings({
    eventId,
    params: {
      fileType: exportType,
      separator: separatorType,
      ...filter,
      ...bookingTableParams,
      salesMode: filter?.booking,
      booking: undefined,
    },
    options: { enabled: false },
  });

  const { data: eventTeams } = useGetEventTeamChannels({
    eventId,
    options: { enabled: isEnabledRequest },
  });

  const { data: viewsStats, isLoading: eventViewsIsLoading } = useGetEventViewsStatsGroupBy({
    eventId,
    params: {
      ...filter,
      booking: undefined,
      groupByDate: eventSalesGroupBy,
    },
    options: { enabled: !!eventId && activeLinearTabDataType === EVENT_REPORT_TAB_TYPE_VIEWS },
  });

  const timeOptions: EventReportTimeOption[] = useMemo(() => {
    if (eventReportState === VIEW_UPCOMING)
      return timeOptionsActions.map((option) => ({
        action: option,
        name: t(`eventReport:${option}Label`),
      }));

    return pastTimeOptionsActions.map((option) => ({
      action: option,
      name: t(`eventReport:${option}Label`),
    }));
  }, [eventReportState, t]);

  const bookingOptions: EventReportBookingOption[] = useMemo(
    () =>
      bookingOptionsActions.map((option) => ({
        action: option,
        name: t(`eventReport:${option}Label`),
      })),
    [t]
  );

  const namedSelectedChannels = useMemo(() => {
    if (!eventTeams) return undefined;
    const namedChannels = filter?.channels
      ?.map((selectedChannelId) => {
        const channelsWithoutTeams = eventTeams.channelsWithoutTeams.map(({ channel }) => ({
          id: channel.id,
          name: channel.name,
        }));

        const teams = eventTeams.teams.flatMap(({ channels }) =>
          channels.map(({ channel }) => ({ id: channel.id, name: channel.name }))
        );

        return [...channelsWithoutTeams, ...teams].find(({ id }) => id === selectedChannelId);
      })
      .filter((channel) => channel !== undefined);
    return namedChannels ?? undefined;
  }, [eventTeams, filter?.channels]);

  const toggleModalBy = useCallback(
    ({ id, type }: { id?: string; type?: EventReportActionType }) => {
      if (id && type) {
        if (EVENT_REPORT_TYPES.includes(type as EventReportDataType)) {
          const selectedOfferById = eventSales?.[type as EventReportDataType].offers?.find((pass) => pass.id === id);
          setSelectedOffer(selectedOfferById);
        }

        if (type === EVENT_REPORT_ADDON_TYPE) {
          const selectedAddon = eventSalesStatsAddons?.addOns.find((addon) => addon.id === id);
          setSelectedOffer(selectedAddon);
        }
      } else {
        setSelectedOffer(undefined);
      }

      if (type === EVENT_REPORT_ADDON_TYPE) setIsAddonModalOpen(!!id);
      if (type === EVENT_REPORT_PASS_TYPE) setIsPassHoldersModalOpen(!!id);
    },
    [eventSales, eventSalesStatsAddons?.addOns]
  );

  const offersTableActions = useMemo(() => {
    return {
      [EVENT_REPORT_PASS_TYPE]: [
        {
          name: t('common:viewHolders'),
          method: toggleModalBy,
        },
      ],
      [EVENT_REPORT_GUEST_LIST_TYPE]: [],
      [EVENT_REPORT_TICKET_TYPE]: [],
      [EVENT_REPORT_BOTTLE_SERVICE_TYPE]: [],
      [EVENT_REPORT_INVITATION_TYPE]: [],
      [EVENT_REPORT_CHECKIN_TYPE]: [],
      [EVENT_REPORT_ADDON_TYPE]: [
        {
          name: t('eventReport:addonModalTitle'),
          method: toggleModalBy,
        },
      ],
    };
  }, [toggleModalBy, t]);

  const handleResendBookingClick = ({ id, email }: BookingActionMethodProps) => {
    setSelectedBookingEmail({ email: email ?? '', id });
  };

  const handleDownloadPDFClick = ({ id }: BookingActionMethodProps) => {
    const url = EVENT_REPORT_DOWNLOAD_PDF_ROUTE.replace(':bookingToken', id);
    window.open(url, '_blank');
  };

  const handleSeeDetails = ({ id, holderCode, isHolder }: BookingActionMethodProps) => {
    if (eventId) {
      const bokingDetailRoute = getBookingDetailsRoute({
        bookingId: id,
        clubSlug,
        companyLegacyId,
        eventId,
        isHolder,
        holderCode,
      });

      navigate(bokingDetailRoute);
    }
  };

  const handleOpenCancelBooking = ({ id }: { id: string }) => {
    setIsCancelModalOpen(true);
    setSelectedBookingCancel(id);
  };

  const handleRefundDetails = ({
    id,
    holderCode,
    ticketQuantity,
    legacyId,
    ticketHolders,
    isNominative,
    onlinePrice,
    offerType,
    buyerFirstName,
    buyerLastName,
  }: BookingActionMethodProps) => {
    if (
      ticketQuantity &&
      ticketHolders &&
      onlinePrice &&
      offerType &&
      isNominative !== undefined &&
      buyerFirstName &&
      buyerLastName
    ) {
      setRefundData({
        isOpenModal: true,
        data: {
          bookingId: id,
          quantity: ticketQuantity,
          holderCode,
          legacyId,
          ticketHolders,
          onlinePrice,
          offerType,
          isNominative,
          buyerFirstName,
          buyerLastName,
        },
      });
    }
  };

  const bookingTableActions: BookingActions = useMemo(() => {
    return [
      ...(isEnabledSeeDetails
        ? [
            {
              id: EVENT_REPORT_ACTION_TYPE_SEE_DETAILS,
              name: t('eventReport:seeDetails'),
              method: handleSeeDetails,
            },
          ]
        : []),
      ...(isEnabledRefundButton
        ? [
            {
              id: EVENT_REPORT_ACTION_TYPE_REFUND,
              name: t('eventReport:refund'),
              method: handleRefundDetails,
            },
            {
              id: EVENT_REPORT_ACTION_TYPE_CANCEL,
              name: t('bookingDetail:cancelBooking'),
              method: handleOpenCancelBooking,
            },
          ]
        : []),
      ...(isEnabledResendEmail
        ? [
            {
              id: EVENT_REPORT_ACTION_TYPE_RESEND_BOOKING,
              name: t('eventReport:resendBooking'),
              method: handleResendBookingClick,
            },
          ]
        : []),
      ...(isEnabledDownloadPDF
        ? [
            {
              id: EVENT_REPORT_ACTION_TYPE_DOWNLOAD_PDF,
              name: t('eventReport:downloadTicketPDFLabel'),
              method: handleDownloadPDFClick,
            },
          ]
        : []),
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const setFilters = (filters: EventReportFilter) => {
    setFilter({
      ...filter,
      ...filters,
    });
  };

  const setQueryParams = ({ channels, dateTime, from, to, bookings }: EventReportQueryParams) => {
    const params = [];
    if (isPublicEventReportPage) params.push([SHARED_REPORT_TOKEN_QUERY_PARAM, getPublicPageToken(PUBLIC_PAGE_TOKEN)]);
    if (channels) params.push(['channels', channels.toString()]);
    if (dateTime) params.push(['datetime', dateTime]);
    if (from && to) {
      params.push(['from', from]);
      params.push(['to', to]);
    }
    if (bookings) params.push(['bookings', bookings]);

    setSearchParams(params as URLSearchParamsInit);
  };

  useEffect(() => {
    if (eventSales && eventSalesStatsAddons) {
      setBookingMixSectionData(
        formatDataBookingMixSection({ data: eventSales, t, bookingFilterValue: bookingOptionSelected?.action })
      );

      setOfferTableSectionData((prev) => ({
        ...prev,
        ...formatDataOfferTableSection({
          data: eventSales,
          isLiveEvent: eventReportState === VIEW_UPCOMING,
          addons: eventSalesStatsAddons,
          offersActions: offersTableActions,
          t,
        }),
      }));
    }
  }, [bookingOptionSelected?.action, eventReportState, eventSales, eventSalesStatsAddons, offersTableActions, t]);

  useEffect(() => {
    if (eventSales && eventSalesGroupedBy)
      setHightlightSectionData(
        formatDataHighlightSection({
          data: eventSales,
          dataGroupBy: eventSalesGroupedBy,
          isLiveEvent: eventReportState === VIEW_UPCOMING,
          isRefundEnabled: isRefundStatsEnabled,
          event,
          t,
          language: i18n.language,
          isTodayActive: timeOptionSelected?.action === ALL_TIME_OPTIONS && eventReportState !== VIEW_PAST,
          bookingFilterValue: bookingOptionSelected?.action,
        })
      );
  }, [
    bookingOptionSelected?.action,
    eventReportState,
    eventSales,
    eventSalesGroupedBy,
    isRefundStatsEnabled,
    i18n.language,
    event,
    t,
    timeOptionSelected?.action,
  ]);

  useEffect(() => {
    setOfferTableSectionData((prev) => ({
      ...prev,
      addonBookings: {
        data: undefined,
        addonTableParams,
        setAddonTableParams,
        isLoading: isLoadingAddonBookings,
        fetchNextPage: fetchNextpageAddonBookings,
        hasNextPage: hasNextPageAddonBookings,
        isFetching: isFetchingAddonBookings,
        isFetchingNextPage: isFetchingNextPageAddonBookings,
      },
    }));
  }, [
    addonBookings,
    addonTableParams,
    event,
    fetchNextpageAddonBookings,
    hasNextPageAddonBookings,
    i18n.language,
    isFetchingAddonBookings,
    isFetchingNextPageAddonBookings,
    isLoadingAddonBookings,
    t,
  ]);

  useEffect(() => {
    if (event && addonBookings)
      setOfferTableSectionData((prev) => ({
        ...prev,
        addonBookings: {
          data: formatDataBookingTableSection({
            data: addonBookings.pages.flatMap((page) => page),
            eventId: event.id,
            eventStatus: eventReportState,
            timezone: event.club.city.timezone,
            language: i18n.language,
            isTicketHolderActive: !!bookingTableParams.search,
            actions: bookingTableActions,
            isSuperAdmin: userState?.isSuperAdmin ?? false,
            t,
          }),
          addonTableParams,
          setAddonTableParams,
          isLoading: isLoadingAddonBookings,
          fetchNextPage: fetchNextpageAddonBookings,
          hasNextPage: hasNextPageAddonBookings,
          isFetching: isFetchingAddonBookings,
          isFetchingNextPage: isFetchingNextPageAddonBookings,
        },
      }));
  }, [
    event,
    addonBookings,
    i18n.language,
    t,
    addonTableParams,
    isLoadingAddonBookings,
    fetchNextpageAddonBookings,
    hasNextPageAddonBookings,
    isFetchingAddonBookings,
    isFetchingNextPageAddonBookings,
    bookingTableParams.search,
    bookingTableActions,
    eventReportState,
    userState?.isSuperAdmin,
  ]);

  useEffect(() => {
    if (
      eventSalesGroupBy &&
      eventSales &&
      eventSalesGroupedBy &&
      activeLinearTabDataType !== EVENT_REPORT_TAB_TYPE_VIEWS
    ) {
      setLineChartSectionData(
        formatDataLinearChartSection({
          data: eventSalesGroupedBy,
          eventSales,
          groupBy: eventSalesGroupBy,
          t,
          bookingFilterValue: bookingOptionSelected?.action,
          dataType: activeLinearTabDataType,
          timezone,
          locale: i18n.language,
        })
      );
    }
  }, [
    bookingOptionSelected?.action,
    eventSales,
    eventSalesGroupBy,
    eventSalesGroupedBy,
    viewsStats,
    t,
    namedSelectedChannels,
    activeLinearTabDataType,
    timezone,
    i18n.language,
  ]);

  useEffect(() => {
    if (attendeesStats && eventSales) {
      setAreaChartSectionData(
        formatDataAreaChartSection({
          data: attendeesStats,
          eventSales,
          t,
          bookingFilterValue: bookingOptionSelected?.action,
          dataType: activeAreaTabDataType,
          salesStats: eventSales,
          timezone,
          locale: i18n.language,
        })
      );
    }
  }, [activeAreaTabDataType, attendeesStats, bookingOptionSelected?.action, eventSales, t, timezone, i18n.language]);

  useEffect(() => {
    if (eventSalesGroupBy && eventSales && viewsStats && activeLinearTabDataType === EVENT_REPORT_TAB_TYPE_VIEWS) {
      setLineChartSectionData(
        formatDataLinearChartSection({
          data: viewsStats,
          eventSales,
          groupBy: eventSalesGroupBy,
          t,
          bookingFilterValue: bookingOptionSelected?.action,
          dataType: activeLinearTabDataType,
          selectedChannels: namedSelectedChannels as { id: string; name: string }[],
          timezone,
          locale: i18n.language,
        })
      );
    }
  }, [
    bookingOptionSelected?.action,
    eventSales,
    eventSalesGroupBy,
    eventSalesGroupedBy,
    viewsStats,
    t,
    namedSelectedChannels,
    activeLinearTabDataType,
    timezone,
    i18n.language,
  ]);

  useEffect(() => {
    if (eventSalesChannels && eventSales) {
      setChannelSectionData(
        formatDataChannelSection({
          data: eventSalesChannels,
          eventSales,
          bookingFilterValue: bookingOptionSelected?.action,
          t,
          isLiveEvent: eventReportState === VIEW_UPCOMING,
          tab: activeChannelTab,
        })
      );
    }
  }, [bookingOptionSelected?.action, eventSales, eventSalesChannels, t, activeChannelTab, eventReportState]);

  useEffect(() => {
    if (event && eventBookings && admissions && addons) {
      setBookingTableSectionData((prev) => ({
        ...prev,
        data: formatDataBookingTableSection({
          data: eventBookings.pages.flatMap((page) => page),
          eventId: event.id,
          eventStatus: eventReportState,
          timezone: event.club.city.timezone,
          language: i18n.language,
          isTicketHolderActive: !!bookingTableParams.search,
          actions: bookingTableActions,
          isSuperAdmin: userState?.isSuperAdmin ?? false,
          t,
        }),
        filters: formatFiltersBookingTableSection({
          admissions,
          addons,
          language: i18n.language,
          isLiveOrPastEvent: eventReportState !== VIEW_UPCOMING,
          t,
        }),
      }));
    }
  }, [
    addons,
    admissions,
    bookingTableActions,
    bookingTableParams.search,
    event,
    eventBookings,
    eventReportState,
    i18n.language,
    userState?.isSuperAdmin,
    t,
  ]);

  useEffect(() => {
    setBookingTableSectionData((prev) => ({
      ...prev,
      fetchNextPage: fetchNexPageBookings,
      isLoading: isLoadingBookings,
      hasNextPage: hasNextPageBookings,
      isFetching: isFetchingBookings,
      isFetchingNextPage: isFetchingNextPageBookings,
    }));
  }, [fetchNexPageBookings, hasNextPageBookings, isFetchingBookings, isFetchingNextPageBookings, isLoadingBookings]);

  useEffect(() => {
    setBookingTableSectionData((prev) => ({ ...prev, separatorType, exportType }));
  }, [separatorType, exportType]);

  useEffect(() => {
    let from, channels, booking;
    let to = eventReportState === VIEW_PAST ? event?.endingTime : undefined;

    if (datetimeParam) {
      timeOptions.forEach((option) => {
        if (option.action === datetimeParam) {
          setTimeOptionSelected(option);
          const datetimeFilter = getDatetimesFromToValues(option.action);
          from = toBackendWithTimezone(datetimeFilter.from, timezone, true);
          to = toBackendWithTimezone(datetimeFilter.to, timezone, true);
        }
      });
    } else if (fromParam && toParam) {
      from = Number(fromParam);
      to = Number(toParam);
      const fromText = moment(from * 1000)
        .tz(timezone)
        .format(DATE_FORMAT_LONG_DATE);
      const toText = moment(to * 1000)
        .tz(timezone)
        .format(DATE_FORMAT_LONG_DATE);
      const timeOptionsSelected: EventReportTimeOption = {
        name: `${fromText} - ${toText}`,
        action: SELECT_RANGE_OPTIONS,
      };
      setTimeOptionSelected(timeOptionsSelected);
    } else {
      const allTimeOption = timeOptions.find((option) => option.action === ALL_TIME_OPTIONS);
      setTimeOptionSelected(allTimeOption);
    }
    if (bookingParam) {
      bookingOptions?.forEach((option) => {
        if (option.action == bookingParam) setBookingOptionSelected(option);
      });
    } else {
      setBookingOptionSelected(bookingOptions[0]);
    }
    if (channelsParam) {
      channels = channelsParam?.split(',');
    }
    if (bookingParam) {
      const param = bookingParam as EventReportBookingOptions;
      if (bookingOptionsActions.includes(param)) booking = param;
    }

    setFilters({ channels, from, to, booking });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    channelsParam,
    eventReportState,
    event?.endingTime,
    datetimeParam,
    fromParam,
    toParam,
    bookingParam,
    timezone,
    timeOptions,
    bookingOptions,
  ]);

  useEffect(() => {
    setLayout({ type: LAYOUT_WITHOUT_SIDEBAR });
    setPageIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  return (
    <EventReportContext.Provider
      value={{
        attendeesStats,
        areaChartSectionData,
        event: event ?? undefined,
        eventIsLoading,
        eventSales,
        eventSalesIsLoading,
        eventSalesGroupedBy,
        eventSalesGroupedByIsLoading,
        attendeesStatsIsLoading,
        eventSalesGroupBy,
        offerTableSectionData,
        bookingMixSectionData,
        hightlightSectionData,
        lineChartSectionData,
        bookingTableSectionData,
        bookingTableParams,
        exportData,
        isExportBookingLoading,
        filter,
        channelSectionData,
        eventSalesChannelsIsLoading,
        eventChannels,
        eventTeams,
        timeOptions,
        timeOptionSelected,
        bookingOptions,
        bookingOptionSelected,
        selectedChannels: filter?.channels ?? [],
        activeLinearTabDataType,
        eventViewsIsLoading,
        isAddonModalOpen,
        isCancelModalOpen,
        setIsCancelModalOpen,
        isPassHoldersModalOpen,
        selectedOffer,
        selectedBookingCancel,
        selectedBookingEmail,
        activeChannelTab,
        refetchExportFile,
        setEventSalesGroupBy,
        setBookingTableParams,
        setFilters,
        setQueryParams,
        setActiveLinearTabDataType,
        toggleModalBy,
        setSelectedBookingEmail,
        refundData,
        setRefundData,
        setActiveChannelTab,
        activeAreaTabDataType,
        setActiveAreaTabDataType,
      }}>
      {!pageIsLoading && children}
    </EventReportContext.Provider>
  );
};
